import ManagerRegistry from "@/core/battle/ManagerRegistry";
import { DIFFICULTY, EFFECTIVE_COUNT, TERRAINS } from "@/core/constants/battle.constants";
import { type BattleUnit, type Skill, type CombatResult, type BattleConfig, FactionType, TerrainType, OpportunityAttacker } from "@/core/types/battle.types";
import { getHexDistance, idToCoords } from "@/core/utils/hex.utils";


export interface TerrainBonuses {
    hitChanceBonus: number;
    evasionBonus: number;
    powerMult: number;
}

export class CombatCalculator {
    private static config: BattleConfig = DIFFICULTY.normal;

    static setConfig(config: BattleConfig): void {
        this.config = config;
    }

    static getTerrainBonuses(attackerTerrain?: TerrainType, defenderTerrain?: TerrainType): TerrainBonuses {
        if (!attackerTerrain || !defenderTerrain) {
            return { hitChanceBonus: 0, evasionBonus: 0, powerMult: 1 };
        }
        const atk = TERRAINS[attackerTerrain];
        const def = TERRAINS[defenderTerrain];
        return {
            hitChanceBonus: (atk.hitChance || 0) - (def.hitChance || 0),
            evasionBonus:   (def.evasion || 0)   - (atk.evasion || 0),
            powerMult:      (atk.powerMult || 1)  / (def.powerMult || 1),
        };
    }

    static getDistMod(skill: Skill, dist: number): number {

        let distMod = 0;
        if (skill.range > 2) {
            const minOptimal = Math.ceil(skill.range / 2);
            const maxOptimal = minOptimal + 1;

            let deviation = 0;
            if (dist < minOptimal) {
                deviation = minOptimal - dist;
            } else if (dist > maxOptimal) {
                deviation = dist - maxOptimal;
            }

            // 10% штрафу за кожен гекс відхилення від оптимальної зони
            distMod = -(deviation * 10);
        }

        return distMod
        
    }

    static getHitChance(
        attacker: BattleUnit,
        attackSkill: number,
        defender: BattleUnit,
        skill: Skill,
        dist: number,
        terrainBonuses: TerrainBonuses = { hitChanceBonus: 0, evasionBonus: 0, powerMult: 1 },
    ): number {
        const fatigueMod = attacker.currentStamina < (attacker.stats.stamina * 0.2) ? -5 : 0;
        const terrainMod = terrainBonuses.hitChanceBonus - terrainBonuses.evasionBonus;
        
        // --- Розрахунок штрафу за дистанцію (Distance Penalty) ---
        const distMod = this.getDistMod(skill, dist);
        // ---------------------------------------------------------

        const hitChance = attackSkill + (skill.hitChanceBonus || 0) - defender.stats.evasion + fatigueMod + terrainMod + distMod;
        
        return Math.max(5, hitChance);
    }

    static getArmorFactor(armorPenetration: number, armor: number): number {
        const mit = Math.min(0.8, armor / 100);
        return armorPenetration + (1 - armorPenetration) * (1 - mit);
    }

    static getEffectiveCount(count: number): number {
        return Math.pow(count, EFFECTIVE_COUNT);
    }

    static calculateSkillRawDamage(
        attacker: BattleUnit,
        defender: BattleUnit,
        skill: Skill,
        isAverage: boolean = true,
        terrainBonuses: TerrainBonuses = { hitChanceBonus: 0, evasionBonus: 0, powerMult: 1 }
    ): number {
        const powerMult = (skill.powerMult || 1) * terrainBonuses.powerMult;
        return this.calculateRawDamage(
            attacker,
            defender,
            powerMult,
            attacker.stats.armorPenetration + (skill.armorPenBonus || 0),
            isAverage
        );
    }

    static calculateOpportunityRawDamage(attacker: BattleUnit, defender: BattleUnit, isAverage: boolean = true): number {
        return this.calculateRawDamage(attacker, defender, 0.5, 0.1, isAverage);
    }

    static calculateRawDamage(
        attacker: BattleUnit,
        defender: BattleUnit,
        powerMult: number,
        armorPenetration: number,
        isAverage: boolean = true
    ): number {
        const effectiveCount = this.getEffectiveCount(attacker.count);

        const baseDamage = isAverage
            ? (attacker.stats.damageMin + attacker.stats.damageMax) / 2
            : attacker.stats.damageMin + Math.random() * (attacker.stats.damageMax - attacker.stats.damageMin);

        const diffMult = attacker.faction === FactionType.Player
            ? this.config.playerDmgMult
            : this.config.enemyDmgMult;

        const rawDamage = baseDamage * effectiveCount * powerMult * diffMult;

        const pen = armorPenetration || 0;
        const penetratingDamage = rawDamage * pen;
        const rawBlockedDamage = rawDamage * (1 - pen);

        const mitigation = Math.min(0.8, defender.stats.armor / 100);
        const blockedDamage = rawBlockedDamage * (1 - mitigation);

        return Math.round(penetratingDamage + blockedDamage);
    }

    static calculateAttack(
        attacker: BattleUnit,
        defender: BattleUnit,
        skill: Skill,
        attackerTerrain?: TerrainType,
        defenderTerrain?: TerrainType,
        options?: { forcedHit?: boolean }
    ): CombatResult {
        const terrainBonuses = this.getTerrainBonuses(attackerTerrain, defenderTerrain);

        const isRanged = skill.range > 2;
        const unitSkill = isRanged ? attacker.stats.rangedSkill || 0 : attacker.stats.meleeSkill || 0;
        const dist = getHexDistance(attacker, defender);
        const hitChance = this.getHitChance(attacker, unitSkill, defender, skill, dist, terrainBonuses);
        const isHit = options?.forcedHit !== undefined 
            ? options.forcedHit 
            : (Math.random() * 100 <= hitChance);

        if (!isHit) {
            return this.emptyResult(defender, true, `Miss!`);
        }

        const finalDamage = this.calculateSkillRawDamage(attacker, defender, skill, false, terrainBonuses);
        const baseForCrit = terrainBonuses.powerMult !== 0 
            ? Math.round(finalDamage / terrainBonuses.powerMult) 
            : finalDamage;

        // 2. Розрахунок криту
        const totalCritChance = (attacker.stats.critChance || 0) + (skill.critChanceBonus || 0);
        const isCrit = (Math.random() * 100) < totalCritChance;
        const multiplier = (attacker.stats.critMult || 1.2) + (skill.critMultBonus || 0);

        const baseAfterCrit = isCrit ? Math.round(baseForCrit * multiplier) : baseForCrit;

        // 3. Додаємо різницю (бонус від террейну), яка не критувала, назад
        const terrainBonusValue = finalDamage - baseForCrit;
        const totalDamage = baseAfterCrit + terrainBonusValue;

        // const totalCritChance = (attacker.stats.critChance || 0) + (skill.critChanceBonus || 0);
        // const isCrit = (Math.random() * 100) < totalCritChance;
        // const multiplier = (attacker.stats.critMult || 1.2) + (skill.critMultBonus || 0);
        // const totalDamage = isCrit ? Math.round(finalDamage * multiplier) : finalDamage;

        return this.applyDamageToStack(defender, totalDamage, isCrit);
    }

    static checkOpportunityAttackersOnHex(
        movingUnitId: string,
        hexId: string,
        units: Map<string, BattleUnit>,
        alreadyAttackedIds: Set<string>,
    ): OpportunityAttacker[] {
        const movingUnit = units.get(movingUnitId);
        if (!movingUnit) return [];

        const registry = ManagerRegistry.getInstance().entityRegistry;

        const hex: { q: number; r: number; } = registry.getHex(hexId)?.data ?? idToCoords(hexId);
        const attackers: OpportunityAttacker[] = [];

        for (const [unitId, unit] of units.entries()) {
            if (unit.faction === movingUnit.faction) continue;
            if (unit.isDead) continue;
            if (unit.opportunityAttackUsed) continue;
            if (alreadyAttackedIds.has(unitId)) continue;
            if (getHexDistance(hex, unit) > 1) continue;

            const skill = unit.skills[0];
            if (!skill) continue;

            const unitSkill = unit.stats.meleeSkill || 1;
            const hitChance = this.getHitChance(unit, unitSkill, movingUnit, skill, 1);
            const hit = Math.random() * 100 <= hitChance;

            attackers.push({ attackerId: unitId, hit, stamina: unit.stats.stamina });

            if (hit) break; // дальше не считаем
        }

        return attackers;
    }

    private static applyDamageToStack(defender: BattleUnit, damage: number, isCrit: boolean): CombatResult {
        const totalHpBefore = (defender.count - 1) * defender.stats.hp + defender.currentUnitHp;
        const totalHpAfter = Math.max(0, totalHpBefore - damage);

        const newCount = Math.ceil(totalHpAfter / defender.stats.hp);
        const newCurrentHp = totalHpAfter % defender.stats.hp || (totalHpAfter > 0 ? defender.stats.hp : 0);

        return {
            targetId: defender.unitId,
            damage,
            isCrit,
            isMiss: false,
            finalDefenderHp: newCurrentHp,
            finalDefenderCount: newCount,
            isDead: totalHpAfter <= 0,
            details: `${isCrit ? 'CRIT! ' : ''}Dealt ${damage} damage.`
        };
    }

    private static emptyResult(defender: BattleUnit, isMiss: boolean, details: string): CombatResult {
        return {
            targetId: defender.unitId,
            damage: 0,
            isCrit: false,
            isMiss,
            finalDefenderHp: defender.currentUnitHp,
            finalDefenderCount: defender.count,
            isDead: false,
            details
        };
    }
}